import { Button, CardLayout } from '../components/elements'
import React, { Component, createRef } from 'react';
import { RouteComponentProps } from 'react-router'
import { AppState } from '../store'
import { application, getWorkspaceByName, getMembership, getSubscription } from '../store/application/selectors';
import { connect } from 'react-redux'
import { IApplication } from '../store/application/types';
import { projects } from '../store/projects/selectors';
import { IProject } from '../store/projects/types';
import { Link } from 'react-router-dom';
import CreateProjectModal from '../components/CreateProjectModal'
import TimeAgo from 'react-timeago'
import { isEditor, subIsInactive } from '../core/misc';
import { t, getLang } from '../i18n';
import { API_IMPORT_PROJECT_ZIP } from '../api';

const mapStateToProps = (state: AppState) => ({
    application: application(state),
    projects: projects(state)
})
const mapDispatchToProps = {}

interface PropsFromState { application: IApplication; projects: IProject[] }
interface RouterProps extends RouteComponentProps<{ workspaceName: string }> { }
interface PropsFromDispatch { }
interface SelfProps { }
type Props = RouterProps & PropsFromState & PropsFromDispatch & SelfProps

interface State {
    show: boolean
    showAddProjectModal: boolean
    lang: string
    importing: boolean
    // rename-before-import modal
    pendingFile: File | null
    pendingName: string
    showRenameModal: boolean
}

class ProjectsPage extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            show: false,
            showAddProjectModal: false,
            lang: getLang(),
            importing: false,
            pendingFile: null,
            pendingName: '',
            showRenameModal: false,
        }
        this.importFileRef = createRef()
    }

    componentDidMount() {
        window.addEventListener('featmap_lang_change', this.onLangChange)
    }
    componentWillUnmount() {
        window.removeEventListener('featmap_lang_change', this.onLangChange)
    }
    onLangChange = () => this.setState({ lang: getLang() })

    importFileRef: React.RefObject<HTMLInputElement>

    // Step 1 — file picked: parse a friendly name from the filename and show rename modal
    handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0]
        if (!file) return
        e.target.value = '' // allow re-selecting the same file

        // Try to extract a readable name from "featmap-<title>-YYYYMMDD.zip"
        let guessedName = file.name
            .replace(/\.zip$/i, '')
            .replace(/^featmap-/i, '')
            .replace(/-\d{8}$/, '')     // strip trailing date
            .replace(/_/g, ' ')
            .trim()
        if (!guessedName) guessedName = file.name.replace(/\.zip$/i, '')

        this.setState({ pendingFile: file, pendingName: guessedName, showRenameModal: true })
    }

    // Step 2 — user confirmed name: do the actual upload
    confirmImport = () => {
        const { pendingFile, pendingName } = this.state
        if (!pendingFile) return

        const { workspaceName } = this.props.match.params
        const ws = getWorkspaceByName(this.props.application, workspaceName)!

        this.setState({ showRenameModal: false, importing: true })
        API_IMPORT_PROJECT_ZIP(ws.id, pendingFile, pendingName.trim() || undefined)
            .then(res => {
                if (!res.ok) throw new Error('import failed')
                return res.json() as Promise<IProject>
            })
            .then(proj => {
                alert(t('import_zip_ok') + '：' + proj.title)
                this.props.history.push(this.props.location.pathname + '/projects/' + proj.id)
            })
            .catch(() => alert(t('import_zip_fail')))
            .finally(() => this.setState({ importing: false, pendingFile: null }))
    }

    cancelImport = () => this.setState({ showRenameModal: false, pendingFile: null, pendingName: '' })

    render() {
        const { workspaceName } = this.props.match.params
        const ws = getWorkspaceByName(this.props.application, workspaceName)!
        const member = getMembership(this.props.application, ws.id)
        const s = getSubscription(this.props.application, ws.id)
        const viewOnly = !isEditor(member.level) || subIsInactive(s)

        return (
            <div>
                {this.state.showAddProjectModal &&
                    <CreateProjectModal workspaceName={workspaceName} close={() => this.setState({ showAddProjectModal: false })} />
                }

                {/* Rename-before-import modal */}
                {this.state.showRenameModal && (
                    <div className="fixed inset-0 flex items-center justify-center z-50">
                        {/* backdrop */}
                        <div className="absolute inset-0 bg-black opacity-40" onClick={this.cancelImport} />
                        <div className="relative bg-white rounded shadow-lg p-6 w-96 z-50">
                            <h2 className="text-base font-semibold mb-4">{t('import_zip_title')}</h2>
                            <label className="block text-sm mb-1">{t('import_zip_name')}</label>
                            <input
                                className="border rounded w-full px-2 py-1 text-sm mb-4 focus:outline-none focus:border-blue-400"
                                type="text"
                                value={this.state.pendingName}
                                onChange={e => this.setState({ pendingName: e.target.value })}
                                onKeyDown={e => e.key === 'Enter' && this.confirmImport()}
                                autoFocus
                            />
                            <div className="flex justify-end gap-2">
                                <button
                                    className="px-3 py-1 text-sm rounded border border-gray-300 hover:bg-gray-100"
                                    onClick={this.cancelImport}
                                >{t('import_zip_cancel')}</button>
                                <button
                                    className="px-3 py-1 text-sm rounded bg-blue-500 text-white hover:bg-blue-600"
                                    onClick={this.confirmImport}
                                >{t('import_zip_confirm')}</button>
                            </div>
                        </div>
                    </div>
                )}

                <div>
                    <div className="p-2 flex flex-row mb-2 items-center">
                        <div><h3>{t('projects_title')}</h3></div>
                        {!viewOnly &&
                            <div className="flex items-center ml-2 gap-2">
                                <Button title={t('project_new')} primary icon="add" handleOnClick={() => this.setState({ showAddProjectModal: true })} />
                                <input
                                    ref={this.importFileRef}
                                    type="file"
                                    accept=".zip"
                                    style={{ display: 'none' }}
                                    onChange={this.handleImport}
                                />
                                <Button
                                    title={this.state.importing ? t('import_zip_ing') : t('import_zip')}
                                    icon="upload"
                                    handleOnClick={() => this.importFileRef.current && this.importFileRef.current.click()}
                                />
                            </div>
                        }
                    </div>

                    <CardLayout>
                        <div>
                            {(this.props.projects.length > 0) ?
                                <div className="flex flex-col max-w-lg">
                                    <div className="p-2">
                                        {this.props.projects.length} {t('project_count')}
                                    </div>
                                    <div>
                                        {this.props.projects.map(x => (
                                            <div className="p-2" key={x.id}>
                                                <div className="mb-1">
                                                    <b><Link to={this.props.location.pathname + "/projects/" + x.id}>{x.title}</Link></b>
                                                </div>
                                                <div className="text-xs">
                                                    {t('created')} <TimeAgo date={x.createdAt} /> {t('by')} {x.createdByName}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                : <div className="p-2">{t('project_none')}</div>
                            }
                        </div>
                    </CardLayout>
                </div>
            </div>
        )
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ProjectsPage)
